import React, { MouseEvent, ReactNode, useEffect, useState } from 'react';
import { Label } from '../label';
import css from './Field.module.css';
import {
  ValidationMessage,
  ValidationMessageProps,
} from '../validationMessage';
import { FieldState } from '../../models';
import clsx from 'clsx';
import { Paragraph } from '../../../text';

export interface InputImage {
  source: string;
  alt: string;
  onClick?: () => void;
}

export interface FieldProps extends FieldPropsExt {
  children: ReactNode;
  hasFocus: boolean;
  onFocus?: () => void;
}

export interface FieldPropsExt extends ValidationMessageProps {
  label: string;
  required?: boolean;
  disabled?: boolean;
  validate?: boolean;
  trailingImage?: InputImage;
  optionalText?: string;
  onLostFocus?: () => void;
}

const Field: React.FC<FieldProps> = (props: FieldProps) => {
  const { required = false, disabled = false } = props;
  const [currentState, setCurrentState] = useState<FieldState>(
    FieldState.Inactive
  );

  const stateStyle = (): string | undefined => {
    switch (currentState) {
      case FieldState.Disabled:
        return css.disabled;
      case FieldState.Error:
        return css.error;
      case FieldState.Active:
        return css.active;
      case FieldState.Inactive:
      default:
        return css.inactive;
    }
  };

  useEffect(() => {
    if (disabled) {
      setCurrentState(FieldState.Disabled);
    } else if (props.hasFocus) {
      setCurrentState(FieldState.Active);
    } else if (props.errorMessage) {
      setCurrentState(FieldState.Error);
    } else {
      setCurrentState(FieldState.Inactive);
    }
  }, [props.hasFocus, disabled, props.errorMessage]);

  const onClickTrailingImage = (event: MouseEvent<HTMLImageElement>) => {
    if (props.trailingImage?.onClick) {
      props.trailingImage?.onClick();
    }
    event.stopPropagation();
  };

  return (
    <div className={css.field}>
      <Label state={currentState} required={required} text={props.label} />
      {props.optionalText ? (
        <Paragraph
          className={css.optionalText}
          small={true}
          text={props.optionalText}
        />
      ) : null}
      <div
        className={clsx(css.input, stateStyle())}
        onClick={() => props.onFocus && props.onFocus()}
      >
        <div className={css.children}>{props.children}</div>
        {props.trailingImage && (
          <div className={css.trailingImage}>
            <img
              className={css.image}
              src={props.trailingImage.source}
              alt={props.trailingImage.alt}
              onClick={onClickTrailingImage}
            />
          </div>
        )}
      </div>
      <ValidationMessage {...props} />
    </div>
  );
};

export default Field;
